import { Box } from '@mui/material';
import { type ReactNode, useState } from 'react';

import { colors } from 'common';

interface DropZoneProps {
  children: ReactNode;
  onDrop: (files: FileList) => void;
  height?: string;
}

/**
 * Calls onDrop when a file is dropped into the drop zone.
 * If you want to cover the full area of the drop zone, you can do
 * one of the following:
 * -  use position: 'absolute' on the child element with use
 * height: 100%, width: 100%, top: 0, left: 0.
 * -  use children with height: 100%, width: 100%
 *
 * @param props
 * @returns
 */
export function DropZone(props: DropZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const { children, onDrop, height = '25vh' } = props;
  return (
    <Box
      width="100%"
      height={height}
      overflow="hidden"
      position="relative"
      textAlign="center"
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        '&:before': {
          // This before pseudo-element provides the border
          // around the drop zone. It is absolutely positioned and
          // scales up to 2x its size, allowing for larger spacing
          // between dashed borders while retaining a border radius
          // of 1 spacing after scaling up.
          content: '""',
          height: '50%',
          width: '50%',
          position: 'absolute',
          border: `0.5px dashed ${dragOver ? colors.primary.default : colors.extended.gray[85]}`,
          borderRadius: 0.5,
          backgroundColor: dragOver ? '#f0f6ff' : 'inherit',
          transform: 'scale(2)',
          zIndex: 0,
        },
      }}
      onDragEnter={() => setDragOver(true)}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOver(false);
        const { files } = event.dataTransfer;
        onDrop(files);
      }}
      onDragOver={(event) => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      <Box sx={{ zIndex: 1 }} width="100%" height="100%">
        {children}
      </Box>
    </Box>
  );
}
